import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { get, has } from 'lodash-es';

import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { defaultPolylineAttribute, IPolylineAttributes } from './polyline.constants';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'finder-polyline',
    templateUrl: './polyline.component.html',
    styleUrls: ['./polyline.component.scss'],
    standalone: true,
    imports: [
        CommonModule
    ]
})
export class PolylineComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @Input() polyline: string;
    @Input() mobilePolyline: string;
    // for more info how to use it see https://css-tricks.com/scale-svg/#the-viewbox-attribute
    @Input() viewBox = '0 0 1000 50';

    polylineAttributes: IPolylineAttributes;
    mobilePolylineAttributes: IPolylineAttributes;
    activePolyline: IPolylineAttributes;

    private destroy$ = new Subject<void>();

    constructor(
        private loggerService: LoggerService,
        private viewportService: ViewportDetectionService,
        private elementRef: ElementRef
    ) { }

    ngOnInit() {
        this.createPolylines();
    }

    ngAfterViewInit() {
        this.viewportService.onViewportResize()
            .pipe(
                debounceTime(50),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.setActivePolyline());
    }

    // "changes" has to be "any" to avoid a typing error
    ngOnChanges(changes: any): void {
        let changed = false;

        // Look for the possible property changes
        if ((get(changes, 'mobilePolyline.firstChange') === false) && has(changes, 'mobilePolyline.currentValue')) {
            this.mobilePolyline = changes.mobilePolyline.currentValue;
            changed = true;
        }
        if ((get(changes, 'polyline.firstChange') === false) && has(changes, 'polyline.currentValue')) {
            this.polyline = changes.polyline.currentValue;
            changed = true;
        }
        if ((get(changes, 'viewBox.firstChange') === false) && has(changes, 'viewBox.currentValue')) {
            this.viewBox = changes.viewBox.currentValue;
            changed = true;
        }

        if (changed) {
            this.createPolylines();
        }
    }

    createPolylines() {
        this.polylineAttributes = this.parsePolylineProperties(this.polyline);
        this.mobilePolylineAttributes = this.parsePolylineProperties(this.mobilePolyline);
        this.setActivePolyline();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * parse a polyline string provided by the cms with the following format:
     * color:width:points into polyline properties.
     *
     * @returns
     */
    private parsePolylineProperties(polylineValue: string = ''): IPolylineAttributes {
        const attributes = polylineValue.split(':');
        if (attributes.length !== 3) {
            this.loggerService.warn('Invalid Polyline value provided, using default value');

            return defaultPolylineAttribute;
        }

        return {
            color: attributes[0],
            width: attributes[1],
            points: attributes[2]
        };
    }

    private setActivePolyline() {
        this.activePolyline = this.viewportService.isMobile() ?
            this.mobilePolylineAttributes :
            this.polylineAttributes;

        // allow to control the stroke color with css vars
        this.elementRef.nativeElement.style.setProperty('--polyline-stroke', this.activePolyline.color);
    }
}
